import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Task, Message, TaskStatus, Feature, FeaturesByCategory, JsonlUpdate, JsonlMessage, JsonlTodo } from '../types';
import { getMessages, createMessage, startTask, completeTask, deleteTask, stopTask, connectWebSocket, getFeaturesByCategory, updateTaskFeature, connectJsonlWebSocket, getAvailableModels, listEmailDrafts, createEmailDraft, EmailDraft } from '../services/api';
import { LinkedText } from './LinkedText';
import { authHeaders } from '../services/auth';

// Link colour for the dark output panes, where the default link blue is too dim to read.
const OUTPUT_LINK_COLOR = '#93c5fd';

interface Props {
  task: Task;
  onClose: () => void;
  onTaskUpdate: () => void;
}

const TaskDetail: React.FC<Props> = ({ task, onClose, onTaskUpdate }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [claudeOutput, setClaudeOutput] = useState(task.claude_output || '');
  const [currentTask, setCurrentTask] = useState(task);
  const [leftPanelWidth, setLeftPanelWidth] = useState(40); // percentage
  const [isResizing, setIsResizing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [selectedModel, setSelectedModel] = useState<string | null>(task.model || null);
  const [lastActivityTime, setLastActivityTime] = useState<Date | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isWaitingReview, setIsWaitingReview] = useState(false);
  const [timeSinceActivity, setTimeSinceActivity] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [liveStreamingOutput, setLiveStreamingOutput] = useState('');
  // Feature assignment state
  const [featuresData, setFeaturesData] = useState<FeaturesByCategory | null>(null);
  const [showFeatureSelector, setShowFeatureSelector] = useState(false);
  const [isUpdatingFeature, setIsUpdatingFeature] = useState(false);
  // JSONL monitoring state
  const [jsonlMessages, setJsonlMessages] = useState<JsonlMessage[]>([]);
  const [jsonlConnected, setJsonlConnected] = useState(false);
  const [currentTodos, setCurrentTodos] = useState<JsonlTodo[]>([]);
  const [showThinking, setShowThinking] = useState(true);
  const [isStopping, setIsStopping] = useState(false);
  const [jsonlStatus, setJsonlStatus] = useState<string>('');
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [liveActivityCollapsed, setLiveActivityCollapsed] = useState(true); // Collapsed by default
  const [linkedDrafts, setLinkedDrafts] = useState<EmailDraft[]>([]);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftTo, setDraftTo] = useState('');
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftContext, setDraftContext] = useState('');
  const [isFilingDraft, setIsFilingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const jsonlWsRef = useRef<WebSocket | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const jsonlScrollRef = useRef<HTMLDivElement>(null);
  const jsonlContentRef = useRef<HTMLDivElement>(null);
  const jsonlStickRef = useRef(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef<boolean>(true);
  const activityCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const timeUpdateInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    loadMessages();
    loadFeatures();

    // Check initial state from backend
    const checkInitialState = async () => {
      try {
        const { getTask } = await import('../services/api');
        const response = await fetch(`/api/tasks/${task.id}/debug`, { headers: authHeaders() });
        const debugInfo = await response.json();

        // Set processing state based on whether Claude is actually running
        if (debugInfo.has_active_process) {
          setIsProcessing(true);
          setIsWaitingReview(false);
        } else if (currentTask.status === TaskStatus.READY_FOR_REVIEW) {
          // Task marked as ready for review
          setIsProcessing(false);
          setIsWaitingReview(true);
        } else if (currentTask.status === TaskStatus.IN_PROGRESS && debugInfo.output_length > 0) {
          // Task is in progress but no active process - ready for review
          setIsProcessing(false);
          setIsWaitingReview(true);
        } else {
          setIsProcessing(false);
          setIsWaitingReview(false);
        }
      } catch (error) {
        console.error('Error checking initial state:', error);
        // Fallback to simple status check
        setIsProcessing(currentTask.status === TaskStatus.IN_PROGRESS);
      }
    };

    checkInitialState();

    // Delay WebSocket connection slightly to avoid React StrictMode double-mount issues
    const wsTimeout = setTimeout(() => {
      if (isMountedRef.current) {
        // Connect WebSocket for real-time updates
        wsRef.current = connectWebSocket(
          task.id,
          handleWebSocketMessage,
          setWsConnected
        );

        // Also connect JSONL WebSocket for rich Claude Code monitoring
        jsonlWsRef.current = connectJsonlWebSocket(
          task.id,
          handleJsonlMessage,
          setJsonlConnected
        );
      }
    }, 100);

    // Periodically check task status to ensure we have current info
    activityCheckInterval.current = setInterval(async () => {
      try {
        // Fetch both task data and debug info to check for active process
        const [taskResponse, debugResponse] = await Promise.all([
          fetch(`/api/tasks/${task.id}`, { headers: authHeaders() }),
          fetch(`/api/tasks/${task.id}/debug`, { headers: authHeaders() })
        ]);

        const updatedTask = await taskResponse.json();
        const debugInfo = await debugResponse.json();

        setCurrentTask(updatedTask);

        // Sync claude_output from backend if we don't have it locally yet
        if (updatedTask.claude_output && !claudeOutput) {
          setClaudeOutput(updatedTask.claude_output);
        }

        // Use active process status to determine if Claude is still running
        if (updatedTask.status === TaskStatus.IN_PROGRESS) {
          if (debugInfo.has_active_process) {
            // Claude is actively running - keep showing processing
            setIsProcessing(true);
            setIsWaitingReview(false);
          } else if (debugInfo.output_length > 0) {
            // No active process but has output - ready for review
            setIsProcessing(false);
            setIsWaitingReview(true);
          } else {
            // No output yet - still waiting
            setIsProcessing(false);
            setIsWaitingReview(false);
          }
        } else if (updatedTask.status === TaskStatus.READY_FOR_REVIEW) {
          // Claude finished successfully - ready for human review
          setIsProcessing(false);
          setIsWaitingReview(true);
        } else {
          // Task completed or failed
          setIsProcessing(false);
          setIsWaitingReview(false);
        }
      } catch (error) {
        console.error('Error checking task status:', error);
      }
    }, 5000); // Check every 5 seconds

    // Update "time since activity" every second
    timeUpdateInterval.current = setInterval(() => {
      if (lastActivityTime) {
        setTimeSinceActivity(Math.floor((new Date().getTime() - lastActivityTime.getTime()) / 1000));
      }
    }, 1000);

    return () => {
      isMountedRef.current = false;
      clearTimeout(wsTimeout);

      if (activityCheckInterval.current) {
        clearInterval(activityCheckInterval.current);
      }

      if (timeUpdateInterval.current) {
        clearInterval(timeUpdateInterval.current);
      }

      // Close WebSocket connections
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (jsonlWsRef.current) {
        jsonlWsRef.current.close();
        jsonlWsRef.current = null;
      }
    };
  }, [task.id, currentTask.claude_session_id]); // Reconnect when task or session changes

  // Load available models on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        const models = await getAvailableModels();
        setAvailableModels(models);
      } catch (error) {
        console.error('Error loading available models:', error);
      }
    };
    loadModels();
  }, []);

  // Live activity feed: stick to the bottom unless the user scrolled up.
  const updateStickiness = () => {
    const el = jsonlScrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    jsonlStickRef.current = atBottom;
    setShowJumpToLatest(!atBottom);
  };
  const scrollJsonlToBottom = () => {
    const el = jsonlScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  const jumpToLatest = () => {
    jsonlStickRef.current = true;
    setShowJumpToLatest(false);
    scrollJsonlToBottom();
  };
  useEffect(() => {
    // Runs after render, so the new entry is already laid out.
    if (jsonlStickRef.current) scrollJsonlToBottom();
    else updateStickiness();
  }, [jsonlMessages, expandedMessages, liveActivityCollapsed]);
  useEffect(() => {
    // Content can grow without a state change (wrapping on resize, streaming text).
    const content = jsonlContentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (jsonlStickRef.current) scrollJsonlToBottom();
      else updateStickiness();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [liveActivityCollapsed, jsonlMessages.length > 0]);

  useEffect(() => {
    // Auto-scroll to bottom of output
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [claudeOutput]);

  // Fetch email drafts linked to this task when it is ready for review.
  // Poll every 8s so a draft filed just after the task finishes appears quickly.
  useEffect(() => {
    if (
      currentTask.status !== TaskStatus.READY_FOR_REVIEW &&
      currentTask.status !== TaskStatus.IN_PROGRESS
    ) {
      setLinkedDrafts([]);
      return;
    }
    const fetchDrafts = async () => {
      try {
        const res = await listEmailDrafts({ source_task_id: task.id });
        setLinkedDrafts(res.drafts.filter((d) => d.status === 'pending_review' || d.status === 'failed'));
      } catch {
        // non-fatal
      }
    };
    fetchDrafts();
    const id = setInterval(fetchDrafts, 8000);
    return () => clearInterval(id);
  }, [task.id, currentTask.status]);

  const loadMessages = async () => {
    try {
      const msgs = await getMessages(task.id);
      setMessages(msgs);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const loadFeatures = async () => {
    try {
      const data = await getFeaturesByCategory();
      setFeaturesData(data);
    } catch (error) {
      console.error('Error loading features:', error);
    }
  };

  const handleUpdateFeature = async (featureId: string | null) => {
    setIsUpdatingFeature(true);
    try {
      await updateTaskFeature(task.id, featureId);
      setCurrentTask({ ...currentTask, feature_id: featureId || undefined });
      setShowFeatureSelector(false);
      onTaskUpdate();
    } catch (error) {
      console.error('Error updating feature:', error);
      alert('Failed to update feature');
    }
    setIsUpdatingFeature(false);
  };

  const getFeatureName = (featureId: string | undefined): string => {
    if (!featureId || !featuresData) return 'None';
    for (const categoryId of Object.keys(featuresData.features)) {
      const feature = featuresData.features[categoryId]?.find(f => f.id === featureId);
      if (feature) return feature.name;
    }
    return featureId;
  };

  const handleJsonlMessage = (update: JsonlUpdate) => {
    // Update last activity time on any JSONL message
    setLastActivityTime(new Date());

    console.log('[JSONL] Received:', update.type, update);

    switch (update.type) {
      case 'message':
        if (update.data) {
          // Add to JSONL messages list (avoid duplicates by uuid)
          setJsonlMessages(prev => {
            const exists = prev.some(m => m.uuid === update.data!.uuid);
            if (exists) return prev;
            return [...prev, update.data!];
          });

          // Update current todos if present
          if (update.data.todos && update.data.todos.length > 0) {
            setCurrentTodos(update.data.todos);
          }

          // If it's an assistant message with text, it means Claude is actively working
          if (update.data.type === 'assistant' && (update.data.text || update.data.tool_calls)) {
            setIsProcessing(true);
            setIsWaitingReview(false);
          }
        }
        break;

      case 'session_detected':
        console.log(`[JSONL] Detected new session: ${update.session_id}`);
        // Update the task with the detected session ID
        if (update.session_id) {
          setCurrentTask(prev => ({ ...prev, claude_session_id: update.session_id }));
          // Clear old messages and reconnect JSONL WebSocket to new session
          setJsonlMessages([]);
          setCurrentTodos([]);
          setExpandedMessages(new Set());
          setJsonlStatus('New session detected, reconnecting...');
          // Reconnect JSONL WebSocket to monitor new session
          if (jsonlWsRef.current) {
            jsonlWsRef.current.close();
          }
          jsonlWsRef.current = connectJsonlWebSocket(
            task.id,
            handleJsonlMessage,
            setJsonlConnected
          );
        }
        break;

      case 'session_file':
        console.log(`[JSONL] Monitoring session: ${update.session_id} (${update.file})`);
        break;

      case 'status':
        console.log(`[JSONL] Status: ${update.message}`);
        setJsonlStatus(update.message || '');
        break;

      case 'warning':
        console.warn(`[JSONL] Warning: ${update.message}`);
        setJsonlStatus(`⚠️ ${update.message}`);
        break;

      case 'error':
        console.error(`[JSONL] Error: ${update.message}`);
        setJsonlStatus(`❌ ${update.message}`);
        break;
    }
  };

  const handleWebSocketMessage = (data: any) => {
    // Update last activity time on any message
    setLastActivityTime(new Date());

    // Log WebSocket messages for debugging
    console.log('[WebSocket] Received:', data.type, data);

    switch (data.type) {
      case 'processing_started':
        // Claude just started processing a message
        setIsProcessing(true);
        setIsWaitingReview(false);
        setIsSendingMessage(false);
        setLiveStreamingOutput(''); // Clear any previous streaming output
        setLiveActivityCollapsed(false); // Auto-expand to show activity
        break;
      case 'claude_output':
        // For initial task: append to main output
        // For messages: append to live streaming output
        // Check if we have any messages - if not, it's the initial task output
        if (messages.length === 0) {
          // Initial task output - always stream to main output
          setClaudeOutput((prev) => prev + data.content);
        } else {
          // Follow-up message output - show in live stream
          setLiveStreamingOutput((prev) => prev + data.content);
        }
        setIsStarting(false);
        setIsProcessing(true);
        setIsWaitingReview(false);
        break;
      case 'task_update':
      case 'task_started':
        setCurrentTask(data.task);
        setIsStarting(false); // Clear loading state when task starts
        setIsProcessing(true);
        setIsWaitingReview(false);
        setLiveActivityCollapsed(false); // Auto-expand to show activity
        onTaskUpdate();
        break;
      case 'task_completed':
      case 'task_failed':
      case 'task_stopped':
        setCurrentTask(data.task);
        setIsStarting(false);
        setIsProcessing(false);
        setIsWaitingReview(false);
        setIsStopping(false);
        // Update claude output from task data if available
        if (data.task.claude_output && !claudeOutput) {
          setClaudeOutput(data.task.claude_output);
        }
        onTaskUpdate();
        break;
      case 'task_finished':
        // Claude finished but task needs human review
        setCurrentTask(data.task);
        setIsStarting(false);
        setIsProcessing(false);
        setIsWaitingReview(true);
        // Update claude output from task data if available (ensures output is shown without reopening)
        if (data.task.claude_output && !claudeOutput) {
          setClaudeOutput(data.task.claude_output);
        }
        onTaskUpdate();
        break;
      case 'new_message':
        setMessages((prev) => [...prev, data.message]);
        // If it's an AI message, Claude finished processing
        if (data.message.sender === 'ai') {
          setIsProcessing(false);
          setIsWaitingReview(false);
          setLiveStreamingOutput(''); // Clear live streaming output
        }
        break;
      case 'session_detected':
        // New Claude session started (from follow-up message)
        console.log(`[WebSocket] New session detected: ${data.session_id}`);
        if (data.session_id) {
          // Clear old JSONL data
          setJsonlMessages([]);
          setCurrentTodos([]);
          setExpandedMessages(new Set());
          setJsonlStatus('New session detected, reconnecting...');
          setLiveActivityCollapsed(false); // Expand panel to show new activity
          // Update session_id - this triggers useEffect to reconnect JSONL WebSocket
          setCurrentTask(prev => ({ ...prev, claude_session_id: data.session_id }));
        }
        break;
      case 'heartbeat':
        // Backend is alive and processing
        setIsProcessing(data.is_running || false);
        break;
    }
  };

  const handleStartTask = async () => {
    try {
      setIsStarting(true);
      setClaudeOutput('⏳ Starting task...\n\nConnecting to Claude Code...\n');
      await startTask(task.id);
      setCurrentTask({ ...currentTask, status: TaskStatus.IN_PROGRESS });
    } catch (error: any) {
      console.error('Error starting task:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Unknown error';
      alert(`Failed to start task: ${errorMessage}`);
      setClaudeOutput('');
      setIsStarting(false);
    }
  };

  const handleCompleteTask = async () => {
    try {
      await completeTask(task.id);
      setCurrentTask({ ...currentTask, status: TaskStatus.COMPLETED });
      onTaskUpdate();
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Failed to complete task');
    }
  };

  const handleDeleteTask = async () => {
    if (!window.confirm('Are you sure you want to delete this task? This cannot be undone.')) {
      return;
    }

    try {
      await deleteTask(task.id);
      onTaskUpdate();
      onClose(); // Close the modal after deletion
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  const handleRerunTask = async () => {
    try {
      setIsStarting(true);
      // Clear the claude output and show loading state
      setClaudeOutput('⏳ Rerunning task...\n\nClearing previous output...\nConnecting to Claude Code...\n\n');
      await startTask(task.id);
      setCurrentTask({ ...currentTask, status: TaskStatus.IN_PROGRESS });
      onTaskUpdate();
    } catch (error) {
      console.error('Error rerunning task:', error);
      alert('Failed to rerun task');
      setClaudeOutput('');
      setIsStarting(false);
    }
  };

  const handleStopTask = async () => {
    if (!isProcessing || isStopping) return;

    // Confirm before stopping
    if (!window.confirm('Are you sure you want to stop this task? Claude will be interrupted mid-work.')) {
      return;
    }

    try {
      setIsStopping(true);
      await stopTask(task.id);
      setIsProcessing(false);
      setIsWaitingReview(false);
      setClaudeOutput((prev) => prev + '\n\n--- TASK STOPPED BY USER ---\n');
      setCurrentTask({ ...currentTask, status: TaskStatus.FAILED });
      onTaskUpdate();
    } catch (error: any) {
      console.error('Error stopping task:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      alert(`Failed to stop task: ${errorMessage}`);
    } finally {
      setIsStopping(false);
    }
  };

  // ESC key to stop running task
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isProcessing && !isStopping) {
        handleStopTask();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isProcessing, isStopping]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSendingMessage) return;

    const messageContent = newMessage;
    setIsSendingMessage(true);
    setNewMessage(''); // Clear input immediately for better UX

    // Show immediate feedback in Claude output and mark as processing
    setIsProcessing(true);
    setLastActivityTime(new Date());
    setClaudeOutput((prev) => prev + `\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n💬 CONTINUING CONVERSATION\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n📨 You asked: "${messageContent}"\n\n⏳ Sending to AI team member...\n🤖 Waiting for response...\n\n`);

    try {
      const messagePayload: any = {
        task_id: task.id,
        sender: 'human',
        content: messageContent,
      };
      if (selectedModel) {
        messagePayload.model = selectedModel;
      }
      await createMessage(messagePayload);
      await loadMessages();
      setIsSendingMessage(false);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
      setNewMessage(messageContent); // Restore message on error
      setIsSendingMessage(false);
      setIsProcessing(false);
    }
  };

  const handleMouseDown = () => {
    setIsResizing(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    // Limit between 20% and 80%
    if (newWidth >= 20 && newWidth <= 80) {
      setLeftPanelWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove as any);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove as any);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }
          @keyframes blink {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.3;
            }
          }

          /* Mobile responsive styles for TaskDetail */
          @media (max-width: 768px) {
            .task-detail-modal {
              border-radius: 8px !important;
              height: 100% !important;
              max-height: 100vh !important;
              overflow-y: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }

            .task-detail-header {
              padding: 12px !important;
              flex-shrink: 0 !important;
            }

            .task-detail-actions {
              width: 100%;
              justify-content: flex-end;
            }

            .task-detail-actions button {
              padding: 6px 12px !important;
              font-size: 12px !important;
            }

            .task-detail-content {
              flex-direction: column !important;
              overflow: visible !important;
              min-height: 0 !important;
              flex: 1 1 auto !important;
            }

            .task-detail-left-panel {
              width: 100% !important;
              max-height: none !important;
              min-height: auto !important;
              border-bottom: 1px solid #ddd;
              padding: 12px !important;
              flex-shrink: 0 !important;
              overflow-y: visible !important;
            }

            .task-detail-divider {
              display: none !important;
            }

            .task-detail-right-panel {
              width: 100% !important;
              flex: 0 0 auto !important;
              padding: 12px !important;
              min-height: auto !important;
              overflow-y: visible !important;
            }

            /* Live Claude Activity panel - allow full height on mobile */
            .task-detail-right-panel .jsonl-panel {
              max-height: none !important;
            }

            /* JSONL messages scroll area - allow full height on mobile */
            .task-detail-right-panel .jsonl-messages-scroll {
              max-height: none !important;
              overflow-y: visible !important;
            }

            /* AI response area - allow full height on mobile */
            .task-detail-right-panel pre,
            .task-detail-right-panel .claude-output-pre {
              max-height: none !important;
            }

            /* Conversation area - allow full height */
            .task-detail-right-panel > div:nth-of-type(3),
            .task-detail-right-panel .conversation-area {
              flex: 0 0 auto !important;
              overflow-y: visible !important;
              min-height: auto !important;
              max-height: none !important;
            }
          }
        `}
      </style>
      <div className="task-detail-overlay" onClick={onClose} style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '8px',
      }}>
      <div className="task-detail-modal" onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '1200px',
        height: '95%',
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div className="task-detail-header" style={{
          padding: '12px 16px',
          borderBottom: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '16px', wordBreak: 'break-word' }}>{currentTask.title}</h2>
            <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
              {currentTask.ai_team_member}
            </p>
          </div>
          <div className="task-detail-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Close X button */}
            <button
              onClick={onClose}
              style={{
                width: '40px',
                height: '40px',
                backgroundColor: '#f3f4f6',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '28px',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                fontWeight: 'bold',
                lineHeight: '1',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#ef4444';
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.borderColor = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#374151';
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
              title="Close"
              type="button"
            >
              ×
            </button>
            {currentTask.status === TaskStatus.QUEUED && (
              <button
                onClick={handleStartTask}
                disabled={isStarting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isStarting ? '#9ca3af' : '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isStarting ? 'not-allowed' : 'pointer',
                  opacity: isStarting ? 0.7 : 1,
                }}
              >
                {isStarting ? '⏳ Starting...' : '▶ Start Task'}
              </button>
            )}
            {(currentTask.status === TaskStatus.IN_PROGRESS || currentTask.status === TaskStatus.READY_FOR_REVIEW) && !isStarting && (
              <button
                onClick={handleCompleteTask}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                ✓ Mark Complete
              </button>
            )}
            {/* Show Stop button when task is running */}
            {isProcessing && !isStopping && (
              <button
                onClick={handleStopTask}
                title="Press ESC to stop"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                }}
              >
                ⏹ Stop Task (ESC)
              </button>
            )}
            {isStopping && (
              <div style={{
                padding: '8px 16px',
                backgroundColor: '#f59e0b',
                color: '#fff',
                borderRadius: '6px',
                fontSize: '14px',
              }}>
                ⏳ Stopping...
              </div>
            )}
            {isStarting && !isProcessing && (currentTask.status === TaskStatus.IN_PROGRESS || currentTask.status === TaskStatus.READY_FOR_REVIEW) && (
              <div style={{
                padding: '8px 16px',
                backgroundColor: '#9ca3af',
                color: '#fff',
                borderRadius: '6px',
                fontSize: '14px',
              }}>
                ⏳ Starting...
              </div>
            )}
            {currentTask.status === TaskStatus.FAILED && (
              <>
                <button
                  onClick={handleCompleteTask}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  ✓ Mark Complete Anyway
                </button>
                <button
                  onClick={handleRerunTask}
                  disabled={isStarting}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isStarting ? '#9ca3af' : '#f59e0b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isStarting ? 'not-allowed' : 'pointer',
                    opacity: isStarting ? 0.7 : 1,
                  }}
                >
                  {isStarting ? '⏳ Starting...' : '🔄 Rerun Task'}
                </button>
              </>
            )}
            {currentTask.status === TaskStatus.COMPLETED && (
              <button
                onClick={handleRerunTask}
                disabled={isStarting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isStarting ? '#9ca3af' : '#f59e0b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isStarting ? 'not-allowed' : 'pointer',
                  opacity: isStarting ? 0.7 : 1,
                }}
              >
                {isStarting ? '⏳ Starting...' : '🔄 Rerun Task'}
              </button>
            )}
            <button
              onClick={handleDeleteTask}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              🗑️ Delete
            </button>
          </div>
        </div>

        {/* Content */}
        <div ref={containerRef} className="task-detail-content" style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left: Task Info */}
          <div className="task-detail-left-panel" style={{ width: `${leftPanelWidth}%`, padding: '16px', overflowY: 'auto' }}>
            {/* Feature Assignment */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>Feature</h3>
              {showFeatureSelector ? (
                <div style={{ backgroundColor: '#f9fafb', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6b7280' }}>
                    Select a feature to assign this task to:
                  </div>
                  <select
                    onChange={(e) => handleUpdateFeature(e.target.value || null)}
                    disabled={isUpdatingFeature}
                    defaultValue={currentTask.feature_id || ''}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      marginBottom: '8px',
                    }}
                  >
                    <option value="">-- No Feature --</option>
                    {featuresData?.categories.map((category) => (
                      <optgroup key={category.id} label={category.name}>
                        {featuresData.features[category.id]?.map((feature) => (
                          <option key={feature.id} value={feature.id}>
                            {feature.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowFeatureSelector(false)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#6b7280',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => setShowFeatureSelector(true)}
                  style={{
                    backgroundColor: currentTask.feature_id ? '#dbeafe' : '#f9fafb',
                    padding: '12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: '1px solid transparent',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: currentTask.feature_id ? '500' : 'normal', color: currentTask.feature_id ? '#1e40af' : '#6b7280' }}>
                      {getFeatureName(currentTask.feature_id)}
                    </span>
                    <span style={{ fontSize: '12px', color: '#3b82f6' }}>Change</span>
                  </div>
                </div>
              )}
            </div>

            <h3>Task Description</h3>
            <p style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f9fafb', padding: '12px', borderRadius: '6px' }}>
              <LinkedText text={currentTask.description} />
            </p>
          </div>

          {/* Resizable Divider */}
          <div
            className="task-detail-divider"
            onMouseDown={handleMouseDown}
            style={{
              width: '4px',
              cursor: 'col-resize',
              backgroundColor: isResizing ? '#3b82f6' : '#ddd',
              transition: isResizing ? 'none' : 'background-color 0.2s',
              userSelect: 'none',
            }}
          />

          {/* Right: Conversation */}
          <div className="task-detail-right-panel" style={{ width: `${100 - leftPanelWidth}%`, padding: '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header with Status Indicators */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 style={{ margin: 0 }}>Conversation</h3>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '10px',
                    color: wsConnected ? '#10b981' : '#6b7280',
                  }}>
                    <span style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: wsConnected ? '#10b981' : '#6b7280',
                    }}></span>
                    {wsConnected ? 'WS' : 'WS Off'}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '10px',
                    color: jsonlConnected ? '#22c55e' : '#6b7280',
                  }}>
                    <span style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: jsonlConnected ? '#22c55e' : '#6b7280',
                    }}></span>
                    {jsonlConnected ? 'JSONL' : 'JSONL Off'}
                  </div>
                </div>
              </div>
              <div>
                {isProcessing && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 12px',
                    backgroundColor: '#10b981',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#fff',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      animation: 'blink 1s ease-in-out infinite',
                    }}></span>
                    <span>
                      ACTIVE - Processing...
                      {lastActivityTime && (
                        <span style={{ fontSize: '11px', opacity: 0.8, marginLeft: '8px' }}>
                          (last update: {timeSinceActivity}s ago)
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {/* Only show Ready for Review when NOT processing AND (waiting for review OR status is READY_FOR_REVIEW) AND has AI output */}
                {!isProcessing && (isWaitingReview || currentTask.status === TaskStatus.READY_FOR_REVIEW) && (claudeOutput || liveStreamingOutput) && (
                  <div style={{
                    padding: '6px 12px',
                    backgroundColor: '#8b5cf6',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#fff',
                  }}>
                    👀 Ready for Review - Click "Mark Complete"
                  </div>
                )}
                {linkedDrafts.length > 0 && (
                  <a
                    href="/approval-queue"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: '#0ea5e9',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#fff',
                      textDecoration: 'none',
                      fontWeight: 600,
                    }}
                  >
                    📧 {linkedDrafts.length} email draft{linkedDrafts.length === 1 ? '' : 's'} waiting in Approval Queue
                  </a>
                )}
                {linkedDrafts.length === 0 && (currentTask.status === TaskStatus.READY_FOR_REVIEW || currentTask.status === TaskStatus.IN_PROGRESS) && (
                  <button
                    onClick={() => {
                      setDraftSubject(currentTask.title);
                      setDraftBody('');
                      setDraftTo('');
                      setDraftContext(`From task #${currentTask.id}: ${currentTask.title}`);
                      setDraftError(null);
                      setShowDraftModal(true);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: '#64748b',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#fff',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    + File email draft in Approval Queue
                  </button>
                )}
                {!isProcessing && !isWaitingReview && currentTask.status === TaskStatus.IN_PROGRESS && (
                  <div style={{
                    padding: '6px 12px',
                    backgroundColor: '#f59e0b',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#fff',
                  }}>
                    ⏸️ Waiting for activity...
                  </div>
                )}
              </div>
            </div>
            {/* JSONL Real-time Monitoring Panel - Always show for in-progress tasks */}
            {(isProcessing || jsonlMessages.length > 0 || currentTask.status === TaskStatus.IN_PROGRESS) && (
              <div className="jsonl-panel" style={{
                marginBottom: '16px',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #86efac',
                overflow: 'hidden',
                // The right panel is a column flex with overflow hidden. The panel may shrink on a
                // short window, but only through the message list (a flex child that scrolls),
                // never below its content, so the newest entry is not clipped and the Model
                // selector and message box below stay on screen.
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                position: 'relative',
              }}>
                <div
                  role="button"
                  aria-expanded={!liveActivityCollapsed}
                  title={liveActivityCollapsed ? 'Click to show the live activity' : 'Click to hide the live activity'}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    backgroundColor: liveActivityCollapsed ? '#f0fdf4' : '#dcfce7',
                    borderBottom: liveActivityCollapsed ? 'none' : '1px solid #86efac',
                  }}
                  onClick={() => setLiveActivityCollapsed(!liveActivityCollapsed)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#166534' }}>
                      {liveActivityCollapsed ? '▶' : '▼'} 🔍 Live Claude Activity
                    </span>
                    <span style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      backgroundColor: jsonlConnected ? '#22c55e' : '#6b7280',
                      color: '#fff',
                      borderRadius: '4px',
                    }}>
                      {jsonlConnected ? 'Connected' : 'Connecting...'}
                    </span>
                    {currentTask.claude_session_id && (
                      <span style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        backgroundColor: '#3b82f6',
                        color: '#fff',
                        borderRadius: '4px',
                      }}>
                        {currentTask.claude_session_id.substring(0, 8)}...
                      </span>
                    )}
                    {liveActivityCollapsed && jsonlMessages.length > 0 && (
                      <span style={{ fontSize: '11px', color: '#6b7280' }}>
                        ({jsonlMessages.length} messages)
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowThinking(!showThinking); }}
                    style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      backgroundColor: showThinking ? '#3b82f6' : '#e5e7eb',
                      color: showThinking ? '#fff' : '#374151',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    {showThinking ? '🧠 Hide Thinking' : '🧠 Show Thinking'}
                  </button>
                </div>

                {/* Collapsible content */}
                {!liveActivityCollapsed && (<div style={{ padding: '12px', display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
                {/* Current Todos */}
                {currentTodos.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#166534', marginBottom: '4px' }}>
                      📋 Current Tasks:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {currentTodos.map((todo, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          padding: '4px 8px',
                          backgroundColor: todo.status === 'in_progress' ? '#fef3c7' : todo.status === 'completed' ? '#d1fae5' : '#f3f4f6',
                          borderRadius: '4px',
                        }}>
                          <span>{todo.status === 'completed' ? '✅' : todo.status === 'in_progress' ? '⏳' : '⬜'}</span>
                          <span style={{ color: todo.status === 'completed' ? '#059669' : '#374151' }}>
                            {todo.status === 'in_progress' ? todo.activeForm || todo.content : todo.content}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent JSONL Messages */}
                {jsonlMessages.length > 0 && (
                  <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
                  <div ref={jsonlScrollRef} onScroll={updateStickiness} className="jsonl-messages-scroll" style={{ maxHeight: 'min(400px, 50vh)', minHeight: '96px', flex: '1 1 auto', overflowY: 'scroll', paddingBottom: '8px' }}>
                   <div ref={jsonlContentRef}>
                    {jsonlMessages.slice(-15).map((msg, idx, arr) => {
                      const msgKey = msg.uuid || `msg-${idx}`;
                      const isExpanded = expandedMessages.has(msgKey);
                      const isLatest = idx === arr.length - 1;
                      // Newest message shows in full only when short; long ones collapse like older rows.
                      const showFullText = isExpanded || (isLatest && (msg.text || '').length <= 400);
                      const textOnlyShortLatest = isLatest && !isExpanded && showFullText && !(msg.tool_calls && msg.tool_calls.length) && !(msg.tool_results && msg.tool_results.length) && !msg.thinking;
                      // Collapsed preview: about 4 lines.
                      const previewBox = { lineHeight: 1.4, maxHeight: '5.6em', overflow: 'hidden' } as const;
                      const toggleExpand = () => {
                        // Older rows: stay put. Newest row: keep following the bottom.
                        if (!isLatest) jsonlStickRef.current = false;
                        setExpandedMessages(prev => {
                          const next = new Set(prev);
                          if (next.has(msgKey)) next.delete(msgKey);
                          else next.add(msgKey);
                          return next;
                        });
                      };

                      return (
                      <div key={msgKey} style={{
                        marginBottom: '8px',
                        padding: '8px',
                        backgroundColor: msg.type === 'user' ? '#dbeafe' : '#fff',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                      }}
                      onClick={toggleExpand}
                      title={isExpanded ? 'Click to collapse' : 'Click to expand'}
                      >
                        <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
                          <span>{msg.type === 'user' ? '👤 User' : '🤖 Claude'} • {new Date(msg.timestamp).toLocaleTimeString()}</span>
                          {!textOnlyShortLatest && <span style={{ color: '#3b82f6' }}>{isExpanded ? '▼ collapse' : '▶ expand'}</span>}
                        </div>

                        {/* Text content */}
                        {msg.text && (
                          <div style={{ fontSize: '12px', whiteSpace: 'pre-wrap', marginBottom: '4px', overflowWrap: 'anywhere', ...(showFullText ? {} : previewBox) }}>
                            {showFullText ? msg.text : (msg.text.length > 300 ? msg.text.substring(0, 300) + '...' : msg.text)}
                          </div>
                        )}

                        {/* Tool calls */}
                        {msg.tool_calls && msg.tool_calls.length > 0 && (
                          <div style={{ marginTop: '4px' }}>
                            {msg.tool_calls.map((tool, toolIdx) => {
                              const inputStr = tool.input ? JSON.stringify(tool.input, null, isExpanded ? 2 : 0) : '';
                              return (
                              <div key={tool.id || toolIdx} style={{
                                fontSize: '11px',
                                padding: '4px 8px',
                                backgroundColor: '#fef3c7',
                                borderRadius: '4px',
                                marginBottom: '2px',
                                overflow: 'hidden',
                              }}>
                                <span style={{ fontWeight: 'bold' }}>🔧 {tool.name}</span>
                                {inputStr && (
                                  <pre style={{
                                    color: '#6b7280',
                                    margin: '4px 0 0 0',
                                    fontSize: '10px',
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    overflow: 'hidden',
                                    ...(isExpanded ? {} : previewBox),
                                  }}>
                                    {isExpanded ? inputStr : inputStr.substring(0, 300) + (inputStr.length > 300 ? '...' : '')}
                                  </pre>
                                )}
                              </div>
                            )})}
                          </div>
                        )}

                        {/* Tool results */}
                        {msg.tool_results && msg.tool_results.length > 0 && (
                          <div style={{ marginTop: '4px' }}>
                            {msg.tool_results.map((result, resIdx) => {
                              const content = typeof result.content === 'string' ? result.content : JSON.stringify(result.content);
                              return (
                              <div key={result.tool_use_id || resIdx} style={{
                                fontSize: '11px',
                                padding: '4px 8px',
                                backgroundColor: '#d1fae5',
                                borderRadius: '4px',
                                marginBottom: '2px',
                                overflow: 'hidden',
                              }}>
                                <span style={{ fontWeight: 'bold' }}>📤 Tool Result</span>
                                <pre style={{
                                  color: '#6b7280',
                                  margin: '4px 0 0 0',
                                  fontSize: '10px',
                                  whiteSpace: 'pre-wrap',
                                  overflowWrap: 'anywhere',
                                  ...(isExpanded ? { overflow: 'hidden', maxHeight: '300px', overflowY: 'auto' as const } : previewBox),
                                }}>
                                  {isExpanded ? content : content.substring(0, 300) + (content.length > 300 ? '...' : '')}
                                </pre>
                              </div>
                            )})}
                          </div>
                        )}

                        {/* Thinking (if enabled) */}
                        {showThinking && msg.thinking && (
                          <div style={{
                            marginTop: '4px',
                            fontSize: '11px',
                            padding: '4px 8px',
                            backgroundColor: '#ede9fe',
                            borderRadius: '4px',
                            color: '#5b21b6',
                            maxHeight: isExpanded ? 'none' : '60px',
                            overflow: 'hidden',
                          }}>
                            <span style={{ fontWeight: 'bold' }}>🧠 Thinking:</span>
                            <div style={{ whiteSpace: 'pre-wrap', marginTop: '2px' }}>
                              {isExpanded ? msg.thinking : (msg.thinking.length > 150 ? msg.thinking.substring(0, 150) + '...' : msg.thinking)}
                            </div>
                          </div>
                        )}

                        {/* Redacted thinking placeholder (Opus 4.7 stores
                            thinking with content redacted, signature only) */}
                        {showThinking && !msg.thinking && msg.has_redacted_thinking && (
                          <div style={{
                            marginTop: '4px',
                            fontSize: '11px',
                            padding: '4px 8px',
                            backgroundColor: '#f3e8ff',
                            borderRadius: '4px',
                            color: '#6b21a8',
                            fontStyle: 'italic',
                          }}>
                            <span style={{ fontWeight: 'bold' }}>🧠 Thinking:</span>{' '}
                            {msg.redacted_thinking_count && msg.redacted_thinking_count > 1
                              ? `${msg.redacted_thinking_count} thinking blocks `
                              : ''}
                            redacted by API (signature-only — common for Opus 4.7).
                          </div>
                        )}
                      </div>
                    )})}
                   </div>
                  </div>
                  {showJumpToLatest && (
                    <button
                      onClick={jumpToLatest}
                      style={{
                        position: 'absolute', right: '16px', bottom: '8px',
                        padding: '4px 10px', fontSize: '11px', borderRadius: '999px',
                        border: '1px solid #16a34a', backgroundColor: '#16a34a', color: '#fff',
                        cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                      }}
                    >
                      ↓ Jump to latest
                    </button>
                  )}
                  </div>
                )}

                {jsonlMessages.length === 0 && (
                  <div style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic', marginTop: '8px' }}>
                    {jsonlStatus && <div style={{ marginBottom: '4px', color: '#166534' }}>{jsonlStatus}</div>}
                    {!jsonlConnected && 'Connecting to JSONL monitor...'}
                    {jsonlConnected && !currentTask.claude_session_id && !jsonlStatus && 'Waiting for session detection...'}
                    {jsonlConnected && currentTask.claude_session_id && !jsonlStatus && 'Monitoring session, waiting for messages...'}
                  </div>
                )}
                </div>)}
              </div>
            )}

            <div className="conversation-area" style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', minHeight: 0 }}>
              {/* Initial task output */}
              {claudeOutput && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: '#f3f4f6',
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    🤖 AI • {new Date(currentTask.started_at || currentTask.created_at).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                    Initial task response
                  </div>
                  <pre className="claude-output-pre" style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    borderRadius: '4px',
                    fontSize: '12px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}>
                    <LinkedText text={claudeOutput} color={OUTPUT_LINK_COLOR} />
                  </pre>
                </div>
              )}

              {/* Failure: its own message with the time at top, shown even when the task produced no output */}
              {currentTask.error && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: '#fef2f2',
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    🤖 AI • {new Date(currentTask.completed_at || currentTask.updated_at).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#ef4444', fontWeight: 'bold', marginBottom: '4px' }}>Error:</div>
                  <pre style={{
                    backgroundColor: '#fff',
                    color: '#ef4444',
                    padding: '8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    maxHeight: '150px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0,
                  }}>
                    {currentTask.error}
                  </pre>
                </div>
              )}

              {/* Conversation messages */}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    marginBottom: '16px',
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: msg.sender === 'human' ? '#dbeafe' : '#f3f4f6',
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    {msg.sender === 'human' ? '👤 Human' : '🤖 AI'} •{' '}
                    {new Date(msg.created_at).toLocaleString()}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}><LinkedText text={msg.content} /></div>
                  {msg.claude_output && (
                    <pre className="claude-output-pre" style={{
                      marginTop: '8px',
                      padding: '8px',
                      backgroundColor: '#1e293b',
                      color: '#e2e8f0',
                      borderRadius: '4px',
                      fontSize: '12px',
                      maxHeight: '400px',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}>
                      <LinkedText text={msg.claude_output} color={OUTPUT_LINK_COLOR} />
                    </pre>
                  )}
                </div>
              ))}

              {/* Live streaming output while processing */}
              {isProcessing && liveStreamingOutput && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: '#f3f4f6',
                  border: '2px solid #10b981',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                }}>
                  <div style={{ fontSize: '12px', color: '#10b981', marginBottom: '4px', fontWeight: 'bold' }}>
                    🤖 AI • Processing...
                  </div>
                  <pre className="claude-output-pre" style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    borderRadius: '4px',
                    fontSize: '12px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}>
                    <LinkedText text={liveStreamingOutput} color={OUTPUT_LINK_COLOR} />
                  </pre>
                </div>
              )}

              {isSendingMessage && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: '#dbeafe',
                  opacity: 0.7,
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    👤 Human • Sending...
                  </div>
                  <div style={{ fontStyle: 'italic', color: '#666' }}>
                    ⏳ Sending message...
                  </div>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Model Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>
                  Model:
                </label>
                <select
                  value={selectedModel || ''}
                  onChange={(e) => {
                    setSelectedModel(e.target.value || null);
                    setCurrentTask(prev => ({ ...prev, model: e.target.value || undefined }));
                  }}
                  disabled={isSendingMessage}
                  style={{
                    padding: '6px 8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#fff',
                    fontSize: '12px',
                    cursor: isSendingMessage ? 'not-allowed' : 'pointer',
                    opacity: isSendingMessage ? 0.6 : 1,
                  }}
                >
                  <option value="">System default {currentTask.model ? `(${currentTask.model})` : ''}</option>
                  {availableModels.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message Input Row */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    // Ctrl+Enter or Cmd+Enter to send (desktop-friendly)
                    // Regular Enter just adds a new line (mobile-friendly)
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isSendingMessage) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder={isSendingMessage ? 'Sending...' : 'Type a message... (Ctrl+Enter to send)'}
                  disabled={isSendingMessage}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: isSendingMessage ? '#f3f4f6' : '#fff',
                    cursor: isSendingMessage ? 'not-allowed' : 'text',
                    minHeight: '100px',
                    maxHeight: '300px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={isSendingMessage || !newMessage.trim()}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: (isSendingMessage || !newMessage.trim()) ? '#9ca3af' : '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (isSendingMessage || !newMessage.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (isSendingMessage || !newMessage.trim()) ? 0.7 : 1,
                  }}
                >
                  {isSendingMessage ? '⏳ Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    {showDraftModal && (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          backgroundColor: '#1e293b',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          width: '560px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#f1f5f9', fontSize: '16px', fontWeight: 600 }}>File email draft in Approval Queue</h3>
            <button onClick={() => setShowDraftModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '18px' }}>x</button>
          </div>
          <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: '13px' }}>
            The draft will appear in the Approval Queue where you can review and send it.
          </p>
          {draftError && (
            <div style={{ padding: '10px', backgroundColor: '#450a0a', border: '1px solid #dc2626', borderRadius: '6px', color: '#fca5a5', fontSize: '13px', marginBottom: '14px' }}>
              {draftError}
            </div>
          )}
          <label style={{ display: 'block', color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>To (email address)</label>
          <input
            type="email"
            value={draftTo}
            onChange={e => setDraftTo(e.target.value)}
            placeholder="recipient@example.com"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '8px 10px', marginBottom: '14px',
              backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '6px',
              color: '#f1f5f9', fontSize: '13px',
            }}
          />
          <label style={{ display: 'block', color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>Subject</label>
          <input
            type="text"
            value={draftSubject}
            onChange={e => setDraftSubject(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '8px 10px', marginBottom: '14px',
              backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '6px',
              color: '#f1f5f9', fontSize: '13px',
            }}
          />
          <label style={{ display: 'block', color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>Body</label>
          <textarea
            value={draftBody}
            onChange={e => setDraftBody(e.target.value)}
            placeholder="Paste or type the email body here..."
            rows={10}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '8px 10px', marginBottom: '14px',
              backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '6px',
              color: '#f1f5f9', fontSize: '13px', resize: 'vertical', fontFamily: 'inherit',
            }}
          />
          <label style={{ display: 'block', color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>Context for reviewer (optional)</label>
          <input
            type="text"
            value={draftContext}
            onChange={e => setDraftContext(e.target.value)}
            placeholder="Brief note about what to check before approving"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '8px 10px', marginBottom: '20px',
              backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '6px',
              color: '#f1f5f9', fontSize: '13px',
            }}
          />
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowDraftModal(false)}
              style={{ padding: '8px 16px', backgroundColor: '#334155', color: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
            >
              Cancel
            </button>
            <button
              disabled={isFilingDraft || !draftTo.trim() || !draftSubject.trim() || !draftBody.trim()}
              onClick={async () => {
                setIsFilingDraft(true);
                setDraftError(null);
                try {
                  await createEmailDraft({
                    person: (currentTask.ai_team_member || 'zee').toLowerCase(),
                    to_addrs: draftTo.trim(),
                    subject: draftSubject.trim(),
                    body: draftBody.trim(),
                    context: draftContext.trim() || undefined,
                    source_task_id: currentTask.id,
                    created_by: currentTask.created_by || undefined,
                  });
                  setShowDraftModal(false);
                  const res = await listEmailDrafts({ source_task_id: task.id });
                  setLinkedDrafts(res.drafts.filter(d => d.status === 'pending_review' || d.status === 'failed'));
                } catch (e: unknown) {
                  const msg = e instanceof Error ? e.message : 'Failed to file draft';
                  setDraftError(msg);
                } finally {
                  setIsFilingDraft(false);
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: (isFilingDraft || !draftTo.trim() || !draftSubject.trim() || !draftBody.trim()) ? '#334155' : '#0ea5e9',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                cursor: (isFilingDraft || !draftTo.trim() || !draftSubject.trim() || !draftBody.trim()) ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                opacity: (isFilingDraft || !draftTo.trim() || !draftSubject.trim() || !draftBody.trim()) ? 0.5 : 1,
              }}
            >
              {isFilingDraft ? 'Filing...' : 'File in Approval Queue'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default TaskDetail;
